import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../Contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    doc,
    updateDoc,
    onSnapshot,
    addDoc,
    collection,
    query,
    where
} from 'firebase/firestore';
import { db } from '../../../../Services/firebase-config';
import {
    Users,
    BookOpen,
    Plus,
    ArrowLeft,
    CheckCircle
} from 'lucide-react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import MentorLessonCard from './MentorLessonCard';
import ResourceUploadModal from './ResourceUploadModal';
import TeamChat from '../../hackathon/Chat';

const MentorTeamDetails = () => {
    const { currentUser } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [team, setTeam] = useState(location.state?.team || null);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [expandedLesson, setExpandedLesson] = useState(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showChat, setShowChat] = useState(false);
    const [resources, setResources] = useState(location.state?.resources || []);
    const [isLoading, setIsLoading] = useState(!location.state?.resources);
    const [deletingResourceId, setDeletingResourceId] = useState(null);
    const [editingResourceId, setEditingResourceId] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [resourceToDelete, setResourceToDelete] = useState(null);

    useEffect(() => {
        if (!team) {
            navigate('/eventdashboard/mentor-dashboard');
            return;
        }

        let unsubscribeResources = null;

        // Set up real-time listener for team updates
        const teamRef = doc(db, 'hackathonTeams', team.id);
        const unsubscribeTeam = onSnapshot(teamRef, (doc) => {
            if (doc.exists()) {
                const teamData = {
                    id: doc.id,
                    ...doc.data()
                };
                setTeam(teamData);
            }
        });

        // Skip fetching resources if they were already passed from the previous page
        if (location.state?.resources && location.state.resources.length > 0) {
            setIsLoading(false);
            return () => {
                unsubscribeTeam();
            };
        }

        // Only set up resources listener if we need to fetch them
        setIsLoading(true);
        // Fetch resources based on mentorId OR isAdminResource
        const resourcesConstraints = [];

        if (team.mentorId) {
            resourcesConstraints.push(where('mentorId', '==', team.mentorId));
        }

        // Always include admin resources
        resourcesConstraints.push(where('isAdminResource', '==', true));


        // Query 1: Resources by mentorId (if exists)
        if (team.mentorId) {
            const mentorResourcesQuery = query(
                collection(db, 'resources'),
                where('mentorId', '==', team.mentorId)
            );

            unsubscribeResources = onSnapshot(mentorResourcesQuery, (mentorSnapshot) => {
                const mentorResources = mentorSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                // Query 2: Admin resources
                const adminResourcesQuery = query(
                    collection(db, 'resources'),
                    where('isAdminResource', '==', true)
                );

                const unsubscribeAdmin = onSnapshot(adminResourcesQuery, (adminSnapshot) => {
                    const adminResources = adminSnapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }));

                    // Combine and deduplicate resources by id
                    const combinedResources = [...mentorResources, ...adminResources];
                    const uniqueResources = Array.from(
                        new Map(combinedResources.map(resource => [resource.id, resource])).values()
                    );

                    // Apply filtering
                    const filteredResources = uniqueResources.filter(resource =>
                        resource.visibility === "public" ||
                        (resource.allowedTeams && resource.allowedTeams.includes(team.id))
                    );

                    console.log('Fetched resources:', filteredResources);
                    setResources(filteredResources);
                    setIsLoading(false);
                });

                // Store both unsubscribe functions
                return () => {
                    unsubscribeAdmin();
                };
            });
        } else {
            // If no mentorId, just fetch admin resources
            const adminResourcesQuery = query(
                collection(db, 'resources'),
                where('isAdminResource', '==', true)
            );

            unsubscribeResources = onSnapshot(adminResourcesQuery, (snapshot) => {
                const resourcesData = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })).filter(resource =>
                    resource.visibility === "public" ||
                    (resource.allowedTeams && resource.allowedTeams.includes(team.id))
                );

                console.log('Fetched resources:', resourcesData);
                setResources(resourcesData);
                setIsLoading(false);
            });
        }

        return () => {
            unsubscribeTeam();
            if (unsubscribeResources) {
                unsubscribeResources();
            }
        };
    }, [team, navigate, location.state]);

    // Get team leader from members array using createdBy
    const getTeamLeader = () => {
        if (!team?.createdBy || !team?.members) return null;
        return team.members.find(member => member.id === team.createdBy);
    };

    // Helper function to format date for lessons data lookup
    const formatDateForLookup = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Helper function to check if date has resources
    const hasResourcesOnDate = (date) => {
        const dateKey = formatDateForLookup(date);
        return resources.some(resource => resource.date === dateKey);
    };

    // Custom tile content to show resource indicators
    const tileContent = ({ date, view }) => {
        if (view === 'month' && hasResourcesOnDate(date)) {
            return <div className="absolute bottom-1 right-1 w-2 h-2 bg-custom-green-1 rounded-full"></div>;
        }
        return null;
    };

    // Custom tile class name for styling
    const tileClassName = ({ date, view }) => {
        if (view === 'month') {
            const today = new Date();
            const isToday = date.toDateString() === today.toDateString();
            const isSelected = date.toDateString() === selectedDate.toDateString();

            if (isSelected) {
                return 'react-calendar__tile--selected';
            }
            if (isToday) {
                return 'react-calendar__tile--today';
            }
            if (hasResourcesOnDate(date)) {
                return 'react-calendar__tile--has-lessons';
            }
        }
        return null;
    };

    const handleResourceUpload = async (resourceData) => {
        if (!team) return;

        try {
            const mentorName = currentUser.displayName || currentUser.email || 'Your mentor';

            const newResource = {
                title: resourceData.title,
                instructor: mentorName,
                type: resourceData.type,
                status: 'available',
                description: resourceData.description,
                uploadedAt: new Date(),
                date: formatDateForLookup(selectedDate),
                allowedTeams: [team.id],
                mentorId: team.mentorId,
                addedBy: currentUser.uid,
                ...resourceData.resourceDetails
            };

            // Add new resource to the resources collection
            const docRef = await addDoc(collection(db, 'resources'), newResource);

            // Update local state immediately
            const newResourceWithId = {
                id: docRef.id,
                ...newResource
            };
            setResources(prevResources => [...prevResources, newResourceWithId]);

            // Create notification promises for all team members
            const notificationPromises = team.members?.map(member => {
                const notificationData = {
                    message: `${mentorName} has uploaded a new resource "${resourceData.title}" for your team. Check it out to enhance your learning experience!`,
                    read: false,
                    timestamp: new Date(),
                    userId: member.id,
                    resourceId: docRef.id
                };

                return addDoc(collection(db, 'notifications'), notificationData);
            }) || [];

            // Execute notification operations
            await Promise.all(notificationPromises);

            setShowUploadModal(false);

            // Show success message with notification count
            setSuccessMessage(`Resource uploaded successfully! Notifications sent to ${team.members?.length || 0} team members.`);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (error) {
            console.error('Error uploading resource:', error);
        }
    };

    const handleEditLesson = async (resourceId, updatedData) => {
        if (!team) return;

        try {
            setEditingResourceId(resourceId);
            const resourceRef = doc(db, 'resources', resourceId);
            await updateDoc(resourceRef, updatedData);

            // Update local state immediately
            setResources(prevResources =>
                prevResources.map(resource =>
                    resource.id === resourceId
                        ? { ...resource, ...updatedData }
                        : resource
                )
            );

            // Show success message
            setSuccessMessage('Resource updated successfully!');
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (error) {
            console.error('Error editing resource:', error);
            setSuccessMessage('Error updating resource. Please try again.');
            setTimeout(() => setSuccessMessage(''), 3000);
        } finally {
            setEditingResourceId(null);
        }
    };

    const handleDeleteLesson = async (resourceId) => {
        if (!team) return;

        // Find the resource to get its details
        const resource = resources.find(r => r.id === resourceId);
        if (!resource) return;

        // Set the resource to delete and show confirmation modal
        setResourceToDelete(resource);
        setShowDeleteModal(true);
    };

    const confirmDeleteResource = async () => {
        if (!resourceToDelete) return;

        try {
            setDeletingResourceId(resourceToDelete.id);
            console.log('Removing team from allowedTeams array');

            // Get current allowedTeams array and remove this team's ID
            const currentAllowedTeams = resourceToDelete.allowedTeams || [];
            const updatedAllowedTeams = currentAllowedTeams.filter(teamId => teamId !== team.id);

            // Update the resource to remove this team from allowedTeams
            const resourceRef = doc(db, 'resources', resourceToDelete.id);
            await updateDoc(resourceRef, {
                allowedTeams: updatedAllowedTeams,
            });

            // Update local state immediately - remove the resource from the array
            setResources(prevResources =>
                prevResources.filter(resource => resource.id !== resourceToDelete.id)
            );

            // Show success message
            setSuccessMessage(`Resource removed from this team's access successfully!`);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (error) {
            console.error('Error updating resource:', error);
            setSuccessMessage('Error removing resource access. Please try again.');
            setTimeout(() => setSuccessMessage(''), 3000);
        } finally {
            setDeletingResourceId(null);
            setShowDeleteModal(false);
            setResourceToDelete(null);
        }
    };

    const selectedDateKey = formatDateForLookup(selectedDate);

    if (!team) {
        return (
            <div className="min-h-screen bg-gray-50 p-6">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center py-12">
                        <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Team Not Found</h2>
                        <p className="text-gray-600 mb-4">The team you're looking for could not be found.</p>
                        <button
                            onClick={() => navigate('/eventdashboard/mentor-dashboard')}
                            className="text-custom-blue-1 hover:text-blue-700 font-medium"
                        >
                            Return to Dashboard
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header with Back Button */}
                <div className="mb-8">
                    <button
                        onClick={() => navigate('/eventdashboard/mentor-dashboard')}
                        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Dashboard
                    </button>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Team Resource Management</h1>
                    <p className="text-gray-600">Upload and manage learning resources for your team</p>
                </div>

                {/* Success Message */}
                {successMessage && (
                    <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
                        <div className="flex">
                            <CheckCircle className="h-5 w-5 text-green-400" />
                            <div className="ml-3">
                                <p className="text-sm font-medium text-green-800">
                                    {successMessage}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="space-y-8">
                    {showChat ? (
                        <div className="bg-white rounded-xl shadow-sm border p-6">
                            <button className="text-custom-blue-1 mb-4" onClick={() => setShowChat(false)}>← Back to Team</button>
                            <TeamChat teamId={team.id} teamData={team} onBack={() => setShowChat(false)} />
                        </div>
                    ) : null}
                    {/* Team Details Card */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <div className="flex items-start justify-between mb-6">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900 mb-2">{team.name}</h2>
                                <p className="text-gray-600">{team.description}</p>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                <Users className="w-4 h-4" />
                                {team.members?.length || 0} members
                                <button
                                    onClick={() => setShowChat(true)}
                                    className="ml-4 px-3 py-1 bg-custom-blue-1 text-white rounded text-sm"
                                >
                                    View Team Chat
                                </button>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <div>
                                <h3 className="font-semibold text-gray-900 mb-2">Team Leader</h3>
                                {(() => {
                                    const teamLeader = getTeamLeader();
                                    return teamLeader ? (
                                        <>
                                            <p className="text-gray-600">{teamLeader.name}</p>
                                            <p className="text-sm text-gray-500">{teamLeader.email}</p>
                                            {teamLeader.phoneNumber && (
                                                <p className="text-sm text-gray-500">{teamLeader.phoneNumber}</p>
                                            )}
                                        </>
                                    ) : (
                                        <p className="text-gray-500">No team leader assigned</p>
                                    );
                                })()}
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-900 mb-2">Team Size</h3>
                                <p className="text-gray-600">{team.members?.length || 0} / {team.maxMembers || 5} members</p>
                            </div>
                        </div>

                        {/* Team Members Section */}
                        {team.members && team.members.length > 0 && (
                            <div className="mt-6 pt-6 border-t border-gray-200">
                                <h3 className="font-semibold text-gray-900 mb-4">Team Members</h3>
                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {team.members.map((member, index) => (
                                        <div key={member.id || index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                            <div className="w-8 h-8 bg-custom-blue-1 rounded-full flex items-center justify-center text-white text-sm font-medium">
                                                {member.name?.charAt(0)?.toUpperCase() || 'M'}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate">
                                                    {member.name}
                                                    {member.id === team.createdBy && (
                                                        <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                                            Leader
                                                        </span>
                                                    )}
                                                </p>
                                                <p className="text-xs text-gray-500 truncate">{member.email}</p>
                                                {member.skills && member.skills.length > 0 && (
                                                    <p className="text-xs text-blue-600 truncate">
                                                        {member.skills.slice(0, 2).join(', ')}
                                                        {member.skills.length > 2 && '...'}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Calendar and Resources */}
                    <div className="grid lg:grid-cols-2 gap-8">
                        {/* Calendar */}
                        <div className="bg-white rounded-xl shadow-sm border p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-xl font-bold text-gray-900">Resource Calendar</h3>
                                <button
                                    onClick={() => setShowUploadModal(true)}
                                    className="flex items-center gap-2 px-4 py-2 bg-custom-blue-1 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    <Plus className="w-4 h-4" />
                                    Add Resource
                                </button>
                            </div>
                            <div className="calendar-container">
                                {isLoading ? (
                                    <div className="animate-pulse">
                                        <div className="h-8 bg-gray-200 rounded mb-4"></div>
                                        <div className="grid grid-cols-7 gap-1">
                                            {[...Array(35)].map((_, index) => (
                                                <div key={index} className="h-12 bg-gray-200 rounded"></div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <Calendar
                                        onChange={setSelectedDate}
                                        value={selectedDate}
                                        tileContent={tileContent}
                                        tileClassName={tileClassName}
                                        className="react-calendar-custom"
                                    />
                                )}
                            </div>
                        </div>

                        {/* Resources for Selected Date */}
                        <div className="bg-white rounded-xl shadow-sm border p-6">
                            <h3 className="text-xl font-bold text-gray-900 mb-6">
                                Resources for {selectedDate.toLocaleDateString()}
                            </h3>

                            <div className="space-y-4">
                                {isLoading ? (
                                    // Loading skeleton for resources
                                    [...Array(3)].map((_, index) => (
                                        <div key={index} className="animate-pulse">
                                            <div className="flex items-center space-x-4 p-4 border rounded-lg">
                                                <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                                                <div className="flex-1 space-y-3">
                                                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                                                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : resources.filter(resource =>
                                    resource.date === selectedDateKey
                                ).length > 0 ? (
                                    resources
                                        .filter(resource =>
                                            resource.date === selectedDateKey
                                        )
                                        .map(resource => (
                                            <MentorLessonCard
                                                key={resource.id}
                                                lesson={resource}
                                                isExpanded={expandedLesson === resource.id}
                                                onToggle={() => setExpandedLesson(expandedLesson === resource.id ? null : resource.id)}
                                                onEdit={handleEditLesson}
                                                onDelete={handleDeleteLesson}
                                                isDeleting={deletingResourceId === resource.id}
                                                isEditing={editingResourceId === resource.id}
                                            />
                                        ))
                                ) : (
                                    <div className="text-center py-8">
                                        <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                        <p className="text-gray-500">No resources uploaded for this date</p>
                                        <button
                                            onClick={() => setShowUploadModal(true)}
                                            className="mt-4 text-custom-blue-1 hover:text-blue-700 font-medium"
                                        >
                                            Upload your first resource
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Delete Confirmation Modal */}
                {showDeleteModal && resourceToDelete && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-lg max-w-md w-full p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                                    <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 0h12a2 2 0 002-2v-9a2 2 0 00-2-2H6a2 2 0 00-2 2v9a2 2 0 002 2z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">Remove Resource for This Team</h3>
                                    <p className="text-sm text-gray-600">This will remove access for this team</p>
                                </div>
                            </div>

                            <div className="mb-6">
                                <p className="text-gray-700 mb-3">
                                    Are you sure you want to remove "<strong>{resourceToDelete.title}</strong>" from this team?
                                </p>
                            </div>

                            <div className="flex gap-3 justify-end">
                                <button
                                    onClick={() => {
                                        setShowDeleteModal(false);
                                        setResourceToDelete(null);
                                    }}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                    disabled={deletingResourceId === resourceToDelete.id}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDeleteResource}
                                    disabled={deletingResourceId === resourceToDelete.id}
                                    className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {deletingResourceId === resourceToDelete.id ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Removing...
                                        </>
                                    ) : (
                                        'Remove Resource'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Upload Modal */}
                {showUploadModal && (
                    <ResourceUploadModal
                        isOpen={showUploadModal}
                        onClose={() => setShowUploadModal(false)}
                        onUpload={handleResourceUpload}
                        selectedDate={selectedDate}
                    />
                )}
            </div>
        </div>
    );
};

export default MentorTeamDetails;
